import { Form, Input, Button, message, Checkbox } from 'antd';
import {
  type AuthenticatedAuthResponse,
  AuthErrorCodes,
  CompleteSignupErrorCode,
  completeSignup,
  isAuthError,
  signup,
  signupConfirm,
  SignupConfirmErrorCode,
  SignupErrorCode,
} from '@/api/login.v2';
import { signAgreements } from '@/api/ria-agreements';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import './index.scss';
import {
  confirmationCodeRules,
  removeItemWithExpiry,
  setItemWithExpiry,
} from '@wavvest/shared-utils';
import { store } from '@/store';
import { resetUser, setUser } from '@/store/features/userinfo/userinfoSlice';
import { setSentryUser } from '@/sentry';
import { executeRecaptchaV3 } from '@/services/recaptcha-v3';
import { markInitialAppLoadState } from '@/routes/initial-app-load-state';
import { isValidCssColor } from '@/util/theme-color';
import { AdvisorSignupLayout } from './AdvisorSignupLayout';
import {
  AdvisorLoginBrandContext,
  getAdvisorLoginRouteFirmId,
  isSafeImageUri,
  readAdvisorLoginBrandContextForAuthChrome,
} from './brandContext';

const AGREEMENT_IDS = [
  'agreement-01JWVWDCEM6CD14G2G4XNVEYD7', // Wavvest Privacy Policy
  'agreement-01JWVWDCEMBNS4HPAFFSM19RCF', // Wavvest Technologies Platform License Agreement
  'agreement-01JWVWDCEMQR8FFW47FRSX6A9E', // Wavvest Platform Terms and Conditions
];

export const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteToken = (searchParams.get('token') || '').trim();
  const trimmedRouteFirmId = getAdvisorLoginRouteFirmId(location.pathname, location.search);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [setupInstructionsSent, setSetupInstructionsSent] = useState(false);
  const [signUpMidStep, setSignUpMidStep] = useState<
    | {
        email: string;
        cognito_session?: string;
        password: string;
        next_step: 'signup_confirm' | 'complete_signup' | 'check_email';
        invite_token?: string;
      }
    | undefined
  >(undefined);
  const [loginBranding] = useState<AdvisorLoginBrandContext | null>(() =>
    readAdvisorLoginBrandContextForAuthChrome(trimmedRouteFirmId),
  );
  const rawBrandedLogoUri = loginBranding?.logo_uri?.trim() || '';
  const brandedLogoUri =
    rawBrandedLogoUri && isSafeImageUri(rawBrandedLogoUri) ? rawBrandedLogoUri : '';
  const rawBrandedLogoMarkUri = loginBranding?.logo_mark_uri?.trim() || '';
  const brandedLogoMarkUri =
    rawBrandedLogoMarkUri && isSafeImageUri(rawBrandedLogoMarkUri) ? rawBrandedLogoMarkUri : '';
  const rawBrandedHeroImageUri = loginBranding?.background_uri?.trim() || '';
  const brandedHeroImageUri =
    rawBrandedHeroImageUri && isSafeImageUri(rawBrandedHeroImageUri) ? rawBrandedHeroImageUri : '';
  const rawBrandedLandingColor = loginBranding?.landing_color?.trim() || '';
  const brandedLandingColor =
    rawBrandedLandingColor && isValidCssColor(rawBrandedLandingColor) ? rawBrandedLandingColor : '';
  const resolvedHeroTitle = loginBranding?.introduction?.trim() || undefined;

  type SignupFormValues = {
    email: string;
    password: string;
    confirmPassword: string;
  };

  type SignupConfirmationValues = {
    confirmation_code: string;
  };

  const shouldCompleteSignup = (details: unknown) =>
    typeof details === 'object' &&
    details !== null &&
    'next_step' in details &&
    (details as { next_step?: unknown }).next_step === 'complete_signup';

  const persistFirmDataContext = (firmDataContext: unknown) => {
    if (firmDataContext !== null && firmDataContext !== undefined) {
      setItemWithExpiry('firm_data_context', JSON.stringify(firmDataContext), TOKEN_EXPIRY_TIME);
      return;
    }

    removeItemWithExpiry('firm_data_context');
  };

  const persistSignupSession = async (response: AuthenticatedAuthResponse) => {
    message.success('Account created successfully!');
    setItemWithExpiry('access_token', response.access_token, TOKEN_EXPIRY_TIME);
    setItemWithExpiry('user', JSON.stringify(response.user), TOKEN_EXPIRY_TIME);
    persistFirmDataContext(response.firm_data_context);
    store.dispatch(setUser(response.user));
    setSentryUser({
      id: response.user.id,
      username: response.user.name,
    });

    try {
      await signAgreements({
        agreement_ids: AGREEMENT_IDS,
      });
    } catch (error) {
      console.error('Failed to record agreement signing:', error);
      removeItemWithExpiry('access_token');
      removeItemWithExpiry('user');
      store.dispatch(resetUser());
      message.error(
        'We could not record your agreement acceptance. Please try confirming your signup again.',
      );
      return;
    }

    const profileIsIncomplete =
      response.kind === 'profile_incomplete' || response.user.incomplete_profile === true;

    if (profileIsIncomplete) {
      message.success('Please complete your profile');
      navigate('/complete-profile');
      return;
    }

    navigate('/home', {
      state: markInitialAppLoadState(undefined, undefined, { suppressAdvisorName: true }),
    });
  };

  const submitCompleteSignup = async (
    midStep: NonNullable<typeof signUpMidStep>,
    confirmationCode: string,
  ) =>
    completeSignup({
      email: midStep.email,
      password: midStep.password,
      confirmation_code: confirmationCode,
      ...(midStep.invite_token ? { invite_token: midStep.invite_token } : {}),
    });

  const onFinish = async (values: SignupFormValues) => {
    try {
      setLoading(true);
      setSetupInstructionsSent(false);
      const recaptchaToken = await executeRecaptchaV3('signup');
      const response = await signup({
        email: values.email,
        password: values.password,
        recaptcha_token: recaptchaToken,
      });
      const nextStep = response.next_step ?? 'signup_confirm';
      if (nextStep === 'check_email') {
        message.success('Check your email');
        setSignUpMidStep(undefined);
        setSetupInstructionsSent(true);
        return;
      }

      message.success('Please check your email for a confirmation code');
      if (nextStep === 'complete_signup') {
        message.info(
          'We resent your verification code. Enter the code from your email to finish signing up.',
        );
      }
      setSignUpMidStep({
        email: values.email,
        cognito_session: nextStep === 'signup_confirm' ? response.cognito_session : undefined,
        password: values.password,
        next_step: nextStep,
        ...(inviteToken ? { invite_token: inviteToken } : {}),
      });
    } catch (error) {
      if (isAuthError(error)) {
        switch (error.code as SignupErrorCode) {
          case AuthErrorCodes.INVALID_PARAMETERS:
            message.error('Please enter a valid email and password');
            break;
          case AuthErrorCodes.RECAPTCHA_FAILED:
            message.error(error.message || 'reCAPTCHA verification failed.');
            break;
          case AuthErrorCodes.EMAIL_EXISTS:
            message.error(
              error.message ||
                'An account with this email already exists. Please log in or reset your password.',
            );
            break;
          default:
            message.error('Unknown auth error occurred, please contact our support');
        }
      } else if (error instanceof Error && error.message.includes('reCAPTCHA')) {
        message.error('reCAPTCHA verification failed. Please try again.');
      } else {
        message.error(
          'An unexpected error occurred, please try again later or contact our support',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmitConfirmationCode = async (values: SignupConfirmationValues) => {
    if (signUpMidStep === undefined) {
      return;
    }
    setLoading(true);
    try {
      if (signUpMidStep.next_step === 'complete_signup' || !signUpMidStep.cognito_session?.trim()) {
        const response = await submitCompleteSignup(signUpMidStep, values.confirmation_code);
        await persistSignupSession(response);
        return;
      }

      try {
        const response = await signupConfirm({
          cognito_session: signUpMidStep.cognito_session,
          email: signUpMidStep.email,
          confirmation_code: values.confirmation_code,
          ...(signUpMidStep.invite_token ? { invite_token: signUpMidStep.invite_token } : {}),
        });
        await persistSignupSession(response);
      } catch (error) {
        if (
          isAuthError(error) &&
          error.code === AuthErrorCodes.INVALID_PARAMETERS &&
          shouldCompleteSignup(error.details)
        ) {
          setSignUpMidStep({
            ...signUpMidStep,
            cognito_session: undefined,
            next_step: 'complete_signup',
          });
          const response = await submitCompleteSignup(signUpMidStep, values.confirmation_code);
          await persistSignupSession(response);
          return;
        }
        throw error;
      }
    } catch (error) {
      if (isAuthError(error)) {
        switch (error.code as SignupConfirmErrorCode | CompleteSignupErrorCode) {
          case AuthErrorCodes.INVALID_CODE:
            message.error('The confirmation code is invalid');
            break;
          case AuthErrorCodes.CODE_EXPIRED:
            message.error('The confirmation code has expired');
            break;
          case AuthErrorCodes.INVALID_PARAMETERS:
            message.error(
              error.message || 'Please confirm your email with the latest verification code.',
            );
            break;
          case AuthErrorCodes.RATE_LIMITED:
            message.error('Too many attempts. Please wait before trying again.');
            break;
          case AuthErrorCodes.USER_NOT_FOUND:
            message.error('We could not find your signup session. Please start over.');
            break;
          case AuthErrorCodes.INVALID_CREDENTIALS:
            message.error('Please re-enter your email and password.');
            break;
          default:
            message.error('Unknown auth error occurred, please contact our support');
            break;
        }
      } else {
        message.error(
          'An unexpected error occurred, please try again later or contact our support',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdvisorSignupLayout
      activeStep={signUpMidStep ? 2 : 1}
      logoSrc={brandedLogoUri || undefined}
      logoFallbackSrc={brandedLogoMarkUri || undefined}
      logoAlt={loginBranding?.name || 'Logo'}
      brandColor={brandedLandingColor || undefined}
      heroImageSrc={brandedHeroImageUri || undefined}
      heroTitle={resolvedHeroTitle}
    >
      {signUpMidStep === undefined && !setupInstructionsSent && (
        <div className="advisor-signup-form-block signup">
          <div className="advisor-signup-copy">
            <h1>Create your account</h1>
            <p>Set up your advisor dashboard to manage clients and portfolios.</p>
          </div>

          <Form
            className="advisor-signup-form"
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
            size="large"
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input placeholder="your.email@example.com" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter your password' },
                {
                  min: 8,
                  message: 'Password must be at least 8 characters',
                },
                () => ({
                  validator(_, value) {
                    if (!/[A-Z]/.test(value)) {
                      return Promise.reject(new Error('Password must contain upper case letters'));
                    }
                    if (!/[a-z]/.test(value)) {
                      return Promise.reject(new Error('Password must contain lower case letters'));
                    }
                    if (!/[0-9]/.test(value)) {
                      return Promise.reject(new Error('Password must contain number'));
                    }
                    if (!/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/.test(value)) {
                      return Promise.reject(
                        new Error(
                          'Password must contain special characters: ^$*.[]{}()?"!@#%&/\\,><\':;|_~`=+-',
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Enter your password" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                {
                  required: true,
                  message: 'Please confirm your password',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm your password" />
            </Form.Item>

            <Checkbox
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="custom-checkbox advisor-signup-agreement"
            >
              <span>
                I agree to the{' '}
                <a href="https://wavvest-public-documents.s3.us-west-2.amazonaws.com/Wavvest/Wavvest+Platform+Terms+and+Conditions.pdf">
                  Terms and Conditions
                </a>
                , <a href="https://www.wavvest.com/privacypolicy">Privacy Policy</a>, and{' '}
                <a href="https://wavvest-public-documents.s3.us-west-2.amazonaws.com/Wavvest/Wavvest+Technologies%2C+LLC+Platform+License+Agreement.pdf">
                  Platform License Agreement
                </a>
              </span>
            </Checkbox>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!termsAccepted}
                className="advisor-signup-primary-button"
              >
                Create Account
              </Button>
            </Form.Item>

            <p className="advisor-signup-switch">
              Already have an account? <a onClick={() => navigate('/login')}>Sign in</a>
            </p>
          </Form>
        </div>
      )}
      {signUpMidStep === undefined && setupInstructionsSent && (
        <div className="advisor-signup-form-block signup-status-content">
          <div className="advisor-signup-copy">
            <h1>Check your email</h1>
            <p>
              We sent instructions to finish setting up your Wavvest account. Use the latest email
              you received, then return here to sign in.
            </p>
          </div>
          <div className="advisor-signup-form">
            <div className="signup-button-group">
              <Button
                type="primary"
                onClick={() => navigate('/login')}
                className="advisor-signup-primary-button"
              >
                Sign in
              </Button>
              <Button
                onClick={() => setSetupInstructionsSent(false)}
                className="advisor-signup-secondary-button"
              >
                Use a different email
              </Button>
            </div>
          </div>
        </div>
      )}
      {signUpMidStep && (
        <div className="advisor-signup-form-block advisor-confirmation-block">
          <div className="advisor-signup-copy">
            <h1>Verify your email</h1>
            <p>Enter the confirmation code we sent to {signUpMidStep.email}.</p>
          </div>
          <Form
            className="advisor-signup-form"
            layout="vertical"
            onFinish={onSubmitConfirmationCode}
            requiredMark={false}
            size="large"
          >
            {signUpMidStep.next_step === 'complete_signup' && (
              <p className="advisor-signup-helper-text">
                We resent your verification code. Enter the code from your email to finish signing
                up.
              </p>
            )}
            <Form.Item
              name="confirmation_code"
              label="Confirmation Code"
              rules={confirmationCodeRules}
            >
              <Input maxLength={6} />
            </Form.Item>

            <Form.Item shouldUpdate>
              {({ getFieldsError, getFieldsValue }) => {
                const { confirmation_code } = getFieldsValue();
                const hasErrors = getFieldsError().some(({ errors }) => errors.length > 0);
                const isIncomplete = !confirmation_code;
                return (
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={hasErrors || isIncomplete}
                    className="advisor-signup-primary-button"
                  >
                    Submit
                  </Button>
                );
              }}
            </Form.Item>
            <Form.Item>
              <Button
                onClick={() => setSignUpMidStep(undefined)}
                className="advisor-signup-secondary-button"
              >
                Back
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}
    </AdvisorSignupLayout>
  );
};

const TOKEN_EXPIRY_TIME = 8 * 60 * 60 * 1000;
